import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdminHeader } from '../admin-header/admin-header';
import { AdminSidebar } from '../admin-sidebar/admin-sidebar';
import * as XLSX from 'xlsx';

const baseUrl = 'https://logs.proofile.com.ng/api';

@Component({
  selector: 'app-patients',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminHeader, AdminSidebar],
  templateUrl: './patients.html',
  styleUrls: ['./patients.css']
})
export class PatientsComponent implements OnInit {
  patients: any[] = [];
  wards: any[] = [];
  companies: any[] = [];

  searchTerm = '';
  loading = false;
  feedbackMessage = '';
  feedbackType: 'success' | 'error' = 'success';

  newCompanyName = '';
  currentPage = 1;
  pageSize = 10;

  showPatientModal = false;
  showCompanyModal = false;
  showPreview = false;
  previewData: any[] = [];

  form = {
    surname: '',
    other_names: '',
    sex: '',
    age: null,
    hospital_id: '',
    allergic: '',
    phone: '',
    email: '',
    type: 'out-patient',
    ward_id: null,
    company_id: null
  };

  editingId: number | null = null;

  constructor(private http: HttpClient, private router: Router) {}

  ngOnInit() {
    this.fetchPatients();
    this.fetchWards();
    this.fetchCompanies();
  }

  fetchPatients() {
    this.loading = true;
    this.http.get<any[]>(`${baseUrl}/patients`).subscribe({
      next: data => {
        this.patients = data;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showMessage('❌ Failed to load patients', 'error');
      }
    });
  }

  fetchWards() {
    this.http.get<any[]>(`${baseUrl}/wards`).subscribe(data => {
      this.wards = data;
    });
  }

  fetchCompanies() {
    this.http.get<any[]>(`${baseUrl}/companies`).subscribe(data => {
      this.companies = data;
    });
  }

  addCompany() {
    if (!this.newCompanyName.trim()) return;
    this.http.post(`${baseUrl}/companies`, { name: this.newCompanyName }).subscribe({
      next: () => {
        this.newCompanyName = '';
        this.fetchCompanies();
        this.showMessage('✅ Company added!', 'success');
        this.showCompanyModal = false;
      },
      error: () => this.showMessage('❌ Failed to add company.', 'error')
    });
  }

  getWardName(id: number): string {
    const ward = this.wards.find(w => w.id === id);
    return ward ? ward.name : 'N/A';
  }

  getCompanyName(id: number): string {
    const company = this.companies.find(c => c.id === id);
    return company ? company.name : '—';
  }

  get filteredPatients() {
    const term = this.searchTerm.toLowerCase();
    return this.patients.filter(p => {
      const fullName = `${p.surname} ${p.other_names}`.toLowerCase();
      const hospitalId = p.hospital_id?.toLowerCase() || '';
      const patientType = p.type?.toLowerCase() || '';
      const companyName = this.getCompanyName(p.company_id).toLowerCase();

      return (
        fullName.includes(term) ||
        hospitalId.includes(term) ||
        patientType.includes(term) ||
        companyName.includes(term)
      );
    });
  }

  get paginatedPatients() {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredPatients.slice(start, start + this.pageSize);
  }

  trackById(index: number, item: any) {
    return item.id;
  }

  submit() {
    const url = this.editingId
      ? `${baseUrl}/patients/${this.editingId}`
      : `${baseUrl}/patients`;

    const request = this.editingId
      ? this.http.put(url, this.form)
      : this.http.post(url, this.form);

    request.subscribe({
      next: () => {
        this.resetForm();
        this.fetchPatients();
        this.showMessage('✅ Patient saved successfully!', 'success');
        this.showPatientModal = false;
      },
      error: (err) => {
        if (err.status === 409) {
          this.showMessage('⚠️ Hospital ID already exists.', 'error');
        } else {
          this.showMessage('⚠️ Something went wrong.', 'error');
        }
      }
    });
  }

  edit(p: any) {
    this.editingId = p.id;
    this.form = { ...p };
    this.showPatientModal = true;
  }

  delete(id: number) {
    if (confirm('Are you sure you want to delete this patient?')) {
      this.http.delete(`${baseUrl}/patients/${id}`).subscribe({
        next: () => {
          this.fetchPatients();
          this.showMessage('🗑️ Patient deleted.', 'success');
        },
        error: () => this.showMessage('❌ Failed to delete patient.', 'error')
      });
    }
  }

  resetForm() {
    this.form = {
      surname: '',
      other_names: '',
      sex: '',
      age: null,
      hospital_id: '',
      allergic: '',
      phone: '',
      email: '',
      type: 'out-patient',
      ward_id: null,
      company_id: null
    };
    this.editingId = null;
  }

  viewDetails(id: number) {
    this.router.navigate(['/admin/patients', id]);
  }

  showMessage(msg: string, type: 'success' | 'error') {
    this.feedbackMessage = msg;
    this.feedbackType = type;
    setTimeout(() => (this.feedbackMessage = ''), 4000);
  }

  checkHospitalIdUnique() {
    if (!this.form.hospital_id.trim()) return;

    this.http.get<any[]>(`${baseUrl}/patients?hospital_id=${this.form.hospital_id}`).subscribe(data => {
      if (data.length > 0 && (!this.editingId || data[0].id !== this.editingId)) {
        this.showMessage('⚠️ Hospital ID already taken.', 'error');
      }
    });
  }

  exportToCSV() {
    const headers = ['Surname', 'Other Names', 'Sex', 'Age', 'Hospital ID', 'Phone', 'Email', 'Type', 'Ward', 'Company'];
    const rows = this.filteredPatients.map(p => [
      p.surname, p.other_names, p.sex, p.age, p.hospital_id,
      p.phone, p.email, p.type,
      this.getWardName(p.ward_id),
      this.getCompanyName(p.company_id)
    ]);

    const csv = [headers, ...rows].map(r => r.map(val => `"${val}"`).join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `patients_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  handleFileInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const importedData = XLSX.utils.sheet_to_json(sheet);

      if (importedData.length) {
        this.previewData = importedData;
        this.showPreview = true;
      } else {
        this.showMessage('⚠️ No data found in file.', 'error');
      }
    };

    reader.readAsArrayBuffer(file);
  }

  confirmImport() {
    this.http.post(`${baseUrl}/patients/import`, this.previewData).subscribe({
      next: () => {
        this.fetchPatients();
        this.previewData = [];
        this.showPreview = false;
        this.showMessage('✅ Patients imported successfully!', 'success');
      },
      error: () => {
        this.showMessage('❌ Failed to import patients.', 'error');
      }
    });
  }

  cancelImport() {
    this.previewData = [];
    this.showPreview = false;
  }
}
